import type { NavigationMenuItem } from '@nuxt/ui'

/**
 * Navigation menu links structure based on crane.com Shop menu
 * Structured to match NavigationMenuItem type from @nuxt/ui
 *
 * Note: crane.com currently groups Boxed Stationery, Personalized Stationery and
 * Featured Products under a single "Stationery" menu. They are intentionally kept
 * as separate top-level items here; crane.com will be updated to match.
 */
export const createNavigationLinks = (storefrontUrl: string): NavigationMenuItem[] => [
    {
        label: 'Stationery Box Sets',
        to: `${storefrontUrl}/stationery/boxed-stationery-sets`,
        children: [
            {
                label: 'Notes & Cards',
                to: `${storefrontUrl}/stationery/boxed-stationery-sets/notes-and-cards`,
            },
            {
                label: 'Thank You',
                to: `${storefrontUrl}/stationery/boxed-stationery-sets/thank-you`,
            },
            {
                label: 'Letter Sheets',
                to: `${storefrontUrl}/stationery/boxed-stationery-sets/letter-sheets`,
            },
        ],
    },
    {
        label: 'Personalized Stationery',
        to: `${storefrontUrl}/stationery/personalized-stationery`,
        children: [
            {
                label: 'Personalized Notes & Cards',
                to: `${storefrontUrl}/stationery/personalized-stationery/personalized-notes-and-cards`,
            },
            {
                label: 'Business Cards & Calling Cards',
                to: `${storefrontUrl}/stationery/personalized-stationery/business-cards-and-calling-cards`,
            },
            {
                label: 'Jotter Cards',
                to: `${storefrontUrl}/stationery/personalized-stationery/jotter-cards`,
            },
            {
                label: 'Personalized Letter Sheets',
                to: `${storefrontUrl}/stationery/personalized-stationery/personalized-letter-sheets`,
            },
            {
                label: 'Personalized Envelopes',
                to: `${storefrontUrl}/stationery/personalized-stationery/personalized-envelopes`,
            },
        ],
    },
    {
        label: 'Featured',
        to: `${storefrontUrl}/stationery/featured-products`,
        children: [
            {
                label: 'Best Sellers',
                to: `${storefrontUrl}/stationery/featured-products/best-sellers`,
            },
            {
                label: "What's Trending",
                to: `${storefrontUrl}/stationery/featured-products/whats-trending`,
            },
            {
                label: 'Shop by Color',
                to: `${storefrontUrl}/stationery/featured-products/shop-by-color`,
            },
            {
                label: "Children's Stationery",
                to: `${storefrontUrl}/stationery/featured-products/childrens-stationery`,
            },
            {
                label: 'Egyptian Collection',
                to: `${storefrontUrl}/stationery/featured-products/egyptian-collection`,
            },
        ],
    },
    {
        label: 'Wedding',
        to: `${storefrontUrl}/wedding`,
        children: [
            {
                label: 'Before the Wedding',
                to: `${storefrontUrl}/wedding/before-the-wedding`,
                children: [
                    {
                        label: 'Change the Date',
                        to: `${storefrontUrl}/wedding/before-the-wedding/change-the-date`,
                    },
                    {
                        label: 'Save the Date',
                        to: `${storefrontUrl}/wedding/before-the-wedding/save-the-date`,
                    },
                    {
                        label: 'Rehearsal Dinner Invitations',
                        to: `${storefrontUrl}/wedding/before-the-wedding/rehearsal-dinner-invitations`,
                    },
                ],
            },
            {
                label: 'Wedding Invitations',
                to: `${storefrontUrl}/wedding/wedding-invitations`,
                children: [
                    {
                        label: 'Wedding Invitations',
                        to: `${storefrontUrl}/wedding/wedding-invitations/all-wedding-invitations`,
                    },
                    {
                        label: 'Wedding Accessories',
                        to: `${storefrontUrl}/wedding/wedding-invitations/wedding-accessories`,
                    },
                ],
            },
            {
                label: 'Wedding Receptions',
                to: `${storefrontUrl}/wedding/wedding-receptions`,
                children: [
                    {
                        label: 'Escort and Place Cards',
                        to: `${storefrontUrl}/wedding/wedding-receptions/escort-and-place-cards`,
                    },
                    {
                        label: 'Wedding Table Cards',
                        to: `${storefrontUrl}/wedding/wedding-receptions/table-cards`,
                    },
                ],
            },
            {
                label: 'After the Wedding',
                to: `${storefrontUrl}/wedding/after-the-wedding`,
                children: [
                    {
                        label: 'Wedding Thank You Cards',
                        to: `${storefrontUrl}/wedding/after-the-wedding/wedding-thank-you-notes`,
                    },
                    {
                        label: 'Couples Stationery',
                        to: `${storefrontUrl}/wedding/after-the-wedding/couples-stationery`,
                    },
                ],
            },
        ],
    },
    {
        label: 'Business',
        to: `${storefrontUrl}/business`,
        children: [
            {
                label: 'Business Stationery',
                to: `${storefrontUrl}/business/business-stationery`,
                children: [
                    {
                        label: 'Business Cards',
                        to: `${storefrontUrl}/business/business-stationery/business-cards`,
                    },
                    {
                        label: 'Business Notes & Cards',
                        to: `${storefrontUrl}/business/business-stationery/business-notes-and-cards`,
                    },
                    {
                        label: 'Jotter Cards',
                        to: `${storefrontUrl}/business/business-stationery/business-jotter-cards`,
                    },
                    {
                        label: 'Letterhead',
                        to: `${storefrontUrl}/business/business-stationery/business-letterhead`,
                    },
                    {
                        label: 'Business Envelopes',
                        to: `${storefrontUrl}/business/business-stationery/business-envelopes`,
                    },
                ],
            },
            {
                label: 'Premium Paper & Envelopes',
                to: `${storefrontUrl}/business/bulk-business-papers/premium-paper-and-envelopes`,
            },
            {
                label: 'Corporate Holiday Cards',
                to: `${storefrontUrl}/business/business-holiday-cards/corporate-holiday`,
            },
        ],
    },
    {
        label: 'Holiday',
        to: `${storefrontUrl}/holiday`,
        children: [
            {
                label: 'Holiday Cards',
                to: `${storefrontUrl}/holiday/holiday-cards`,
                children: [
                    {
                        label: 'Personalized Holiday Greeting Cards',
                        to: `${storefrontUrl}/holiday/holiday-cards/holiday-greeting-cards`,
                    },
                    {
                        label: 'Boxed Holiday Greeting Cards',
                        to: `${storefrontUrl}/holiday/holiday-cards/boxed-holiday-cards`,
                    },
                    {
                        label: 'Personalized Holiday Photo Mount Cards',
                        to: `${storefrontUrl}/holiday/holiday-cards/holiday-photo-cards`,
                    },
                    {
                        label: 'Boxed Holiday Photo Mount Cards',
                        to: `${storefrontUrl}/holiday/holiday-cards/boxed-holiday-photo-cards`,
                    },
                    {
                        label: 'Holiday Digital Photo Cards',
                        to: `${storefrontUrl}/holiday/holiday-cards/digital-photo-holiday-cards`,
                    },
                    {
                        label: 'Holiday Invitations',
                        to: `${storefrontUrl}/holiday/holiday-cards/personalized-holiday-invitations`,
                    },
                ],
            },
            {
                label: 'Holidays',
                to: `${storefrontUrl}/holiday/holidays`,
                children: [
                    { label: "Mother's Day", to: `${storefrontUrl}/holiday/holidays/mothers-day` },
                    { label: "Father's Day", to: `${storefrontUrl}/holiday/holidays/fathers-day` },
                ],
            },
            {
                label: 'Holiday Extras',
                to: `${storefrontUrl}/holiday/holiday-extras`,
                children: [
                    {
                        label: 'Holiday Stationery',
                        to: `${storefrontUrl}/holiday/holiday-extras/holiday-stationery`,
                    },
                    {
                        label: 'Gift Tags & Enclosures',
                        to: `${storefrontUrl}/holiday/holiday-extras/gift-tags-and-enclosures`,
                    },
                ],
            },
        ],
    },
    {
        label: 'Events',
        to: `${storefrontUrl}/events`,
        children: [
            {
                label: 'Gifts',
                to: `${storefrontUrl}/events/gifts`,
                children: [
                    { label: 'Motif Shop', to: `${storefrontUrl}/events/gifts/motif-shop` },
                    { label: 'Monogram Shop', to: `${storefrontUrl}/events/gifts/monogram-shop` },
                    { label: 'Gifts for Her', to: `${storefrontUrl}/events/gifts/gifts-for-her` },
                    { label: 'Gifts for Him', to: `${storefrontUrl}/events/gifts/gifts-for-him` },
                    {
                        label: 'Gifts for Teachers',
                        to: `${storefrontUrl}/events/gifts/gifts-for-teachers`,
                    },
                    {
                        label: 'Gifts for the Animal Lover',
                        to: `${storefrontUrl}/events/gifts/gifts-for-the-animal-lover`,
                    },
                    {
                        label: 'Gifts for the Insect Lover',
                        to: `${storefrontUrl}/events/gifts/gifts-for-the-insect-lover`,
                    },
                ],
            },
            {
                label: 'Holiday Gift Guide',
                to: `${storefrontUrl}/events/holiday-gift-guide`,
                children: [
                    {
                        label: "Father's Day Gift Guide",
                        to: `${storefrontUrl}/events/holiday-gift-guide/fathers-day-gift-guide`,
                    },
                    {
                        label: "Mother's Day Gift Guide",
                        to: `${storefrontUrl}/events/holiday-gift-guide/mothers-day-gift-guide`,
                    },
                    {
                        label: "Valentine's Day",
                        to: `${storefrontUrl}/events/holiday-gift-guide/valentines-day`,
                    },
                ],
            },
            {
                label: 'Event Essentials',
                to: `${storefrontUrl}/events/event-essentials`,
                children: [
                    {
                        label: 'Moving Announcements',
                        to: `${storefrontUrl}/events/event-essentials/moving`,
                    },
                    {
                        label: 'Save the Date Cards',
                        to: `${storefrontUrl}/events/event-essentials/save-the-date-cards`,
                    },
                    {
                        label: 'Birth Announcements',
                        to: `${storefrontUrl}/events/event-essentials/birth-announcements`,
                    },
                    {
                        label: 'Party Invitations',
                        to: `${storefrontUrl}/events/event-essentials/party-invitations`,
                    },
                    {
                        label: 'Graduation',
                        to: `${storefrontUrl}/events/event-essentials/graduation`,
                    },
                    {
                        label: 'Place Cards',
                        to: `${storefrontUrl}/events/event-essentials/place-cards`,
                    },
                ],
            },
        ],
    },
    {
        label: 'Sympathy',
        to: `${storefrontUrl}/sympathy`,
    },
    {
        label: 'Sale',
        to: `${storefrontUrl}/sale`,
    },
]
